import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, CheckCircle2, Circle, ChevronDown, ChevronRight, FileCheck, ClipboardCheck, Layers } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Loader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

export default function Course() {
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [phases, setPhases] = useState(null);
  const [open, setOpen] = useState(null);
  const [lessons, setLessons] = useState({});

  const load = async () => {
    const s = await api.get("/my/progress-summary");
    setCourse(s.data.course);
    setPhases(s.data.phases);
    const firstOpen = s.data.phases.find((p) => p.unlocked && !p.complete) || s.data.phases[0];
    if (firstOpen) toggle(firstOpen.phase_id);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (pid) => {
    setOpen((o) => (o === pid ? null : pid));
    if (!lessons[pid]) {
      const { data } = await api.get(`/phases/${pid}/lessons`);
      setLessons((m) => ({ ...m, [pid]: data }));
    }
  };

  if (!phases) return <Loader />;
  if (!course) return <div className="text-center py-20 text-muted-foreground">Course is being set up. Check back soon.</div>;

  return (
    <div>
      <div className="mb-8">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold">{course.title}</h1>
          <span className="inline-flex items-center rounded-full bg-brandgreen/10 text-brandgreen font-heading font-extrabold text-lg px-4 py-1 tracking-wide" data-testid="free-badge">FREE</span>
        </div>
        <div className="h-px bg-border my-4" />
        <p className="text-muted-foreground max-w-2xl">{course.description}</p>
      </div>

      <div className="space-y-4">
        {phases.map((p, idx) => {
          const isOpen = open === p.phase_id;
          const locked = p.locked;
          const pct = p.total_lessons ? Math.round((p.completed_lessons / p.total_lessons) * 100) : 0;
          return (
            <div key={p.phase_id} className={cn("bg-white border rounded-2xl shadow-sm overflow-hidden", locked ? "border-border opacity-90" : "border-border")} data-testid={`course-phase-${idx}`}>
              <button onClick={() => !locked && toggle(p.phase_id)} disabled={locked}
                className={cn("w-full flex items-center gap-4 p-5 text-left", locked ? "cursor-not-allowed" : "hover:bg-secondary/40 transition-colors")}
                data-testid={`phase-toggle-${idx}`}>
                <div className={cn("w-11 h-11 rounded-xl flex items-center justify-center font-heading font-bold shrink-0",
                  p.complete ? "bg-brandgreen/10 text-brandgreen" : locked ? "bg-slate-100 text-slate-400" : "bg-teal/10 text-teal")}>
                  {p.complete ? <CheckCircle2 className="w-6 h-6" /> : locked ? <Lock className="w-5 h-5" /> : idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-semibold">{p.title}</h3>
                    {p.complete && <StatusBadge status="approved" />}
                    {p.requires_evidence && !locked && p.evidence_status !== "not_submitted" && <StatusBadge status={p.evidence_status} />}
                  </div>
                  {!locked ? (
                    <div className="flex items-center gap-3 mt-2">
                      <Progress value={pct} className="h-2 max-w-[200px]" />
                      <span className="text-xs text-muted-foreground">{p.completed_lessons}/{p.total_lessons} lessons</span>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">{p.locked_reason}</p>
                  )}
                </div>
                {!locked && (isOpen ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />)}
              </button>

              {isOpen && !locked && (
                <div className="border-t border-border p-5 space-y-2" data-testid={`phase-body-${idx}`}>
                  {(lessons[p.phase_id] || []).length === 0 && <p className="text-sm text-muted-foreground">No lessons published in this phase yet.</p>}
                  {(lessons[p.phase_id] || []).map((l) => (
                    <button key={l.id} onClick={() => nav(`/lesson/${l.id}`)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-secondary/50 transition-colors text-left" data-testid={`lesson-item-${l.id}`}>
                      {l.completed ? <CheckCircle2 className="w-5 h-5 text-brandgreen shrink-0" /> : <Circle className="w-5 h-5 text-muted-foreground shrink-0" />}
                      <span className="flex-1">{l.title}</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}

                  {p.requires_assessment && (
                    <div className="mt-3 p-4 rounded-xl bg-teal/5 border border-teal/20 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <ClipboardCheck className="w-5 h-5 text-teal" />
                        <div>
                          <p className="font-medium text-sm">Phase Assessment</p>
                          <p className="text-xs text-muted-foreground">{p.assessment_passed ? "Passed" : "Complete all lessons, then take the assessment"}</p>
                        </div>
                      </div>
                      <Button size="sm" className="rounded-full bg-teal hover:bg-teal/90" disabled={!p.lessons_done && !p.assessment_passed}
                        onClick={() => nav(`/phase/${p.phase_id}/assessment`)} data-testid={`take-assessment-${idx}`}>
                        {p.assessment_passed ? "Review" : "Take Assessment"}
                      </Button>
                    </div>
                  )}

                  {p.requires_evidence && (
                    <div className="mt-2 p-4 rounded-xl bg-brandgreen/5 border border-brandgreen/20 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <FileCheck className="w-5 h-5 text-brandgreen" />
                        <div>
                          <p className="font-medium text-sm">Evidence Submission</p>
                          <p className="text-xs text-muted-foreground">
                            {p.evidence_status === "approved" ? "Approved — next phase unlocked!" :
                             p.evidence_status === "under_review" ? "Under review by the team" :
                             p.evidence_status === "needs_revision" ? "Needs revision — see feedback" : "Submit your screenshot to complete this phase"}
                          </p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-full" disabled={!p.assessment_passed && p.requires_assessment}
                        onClick={() => nav(`/phase/${p.phase_id}/assessment?tab=evidence`)} data-testid={`submit-evidence-${idx}`}>
                        {p.evidence_status === "not_submitted" ? "Submit" : "View"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
